import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get, post } from '/api.js';
import { Icon } from '/icons.js';
import { WatchQr } from '/watchqr.js';
import { Clock } from '/clocks.js';
import { Frames } from '/history.js';
import { RED, YELLOW, applyShot, clearanceOf, describe, newFrame,
         shotsFor } from '/rules.js';

/**
 * Scoring a match as it is played — the Pro Pool screen.
 *
 * Two taps per shot, deliberately: pick what happened, then CONFIRM. One tap
 * would be faster and is what the first version did, but a mis-tap on a phone
 * held over a table costs a visit, and the pause is where the pot chips get to
 * appear. It is the mockup's flow and it is the right one.
 *
 * Everything about who is at the table, whose colour is whose and whether the
 * frame is over comes from rules.js. This file renders what the reducer says
 * and reports the frame's OUTCOME; it decides nothing itself. That separation
 * is why the mockup's four state-machine bugs cannot exist here.
 *
 * One tap per frame is still available — "just tell me who won" — because most
 * nights nobody wants to log every visit.
 */

/** The frame in progress, folded from whatever shots have been logged. */
function rebuild(live) {
  const [x, y] = live.players;
  const breaker = live.breaks_next === x.user_id ? x : y;
  const away = breaker === x ? y : x;
  let frame = newFrame(breaker.user_id, away.user_id);
  for (const shot of live.shots || []) frame = applyShot(frame, shot);
  return frame;
}

/**
 * Every shot of the match, newest first.
 *
 * Frames that are over are folded from what the server holds; the frame in
 * progress comes from local state, which is ahead of it — shots are posted as
 * they happen but the screen must not wait for a round trip to show what just
 * happened. Without this the log forgot each frame the moment it ended, which
 * is exactly when you want to look back at it.
 */
function matchLog(live, frame) {
  const stored = live.shots_by_frame || {};
  const current = live.frames.length + 1;
  const out = [];
  const opened = (n) => ({ frame: n, player: null, group: null,
                           text: 'Open table', start: true });
  for (let n = 1; n < current; n += 1) {
    const shots = stored[String(n)] || [];
    if (!shots.length) continue;
    out.push(opened(n));
    // The break alternates and player[0] broke the first frame, which is the
    // same rule the server uses to work out who breaks next.
    const breaker = live.players[(n - 1) % 2];
    const away = live.players[n % 2];
    let f = newFrame(breaker.user_id, away.user_id);
    for (const shot of shots) f = applyShot(f, shot);
    for (const h of f.history) out.push({ ...h, frame: n });
  }
  if (frame) {
    out.push(opened(current));
    for (const h of frame.history) out.push({ ...h, frame: current });
  }
  return out.reverse();
}

const emptyPot = () => ({ [RED]: 0, [YELLOW]: 0, black: false });
const potIsEmpty = (p) => !p[RED] && !p[YELLOW] && !p.black;

function Score({ live, frame, me }) {
  return html`
    <div class="scoreline" data-testid="live-score">
      ${live.players.map((p, i) => html`
        <${ScoreSide} key=${p.user_id} player=${p} frame=${frame} me=${me} />
        ${i === 0 && html`<div class="dash">—</div>`}`)}
    </div>`;
}

function ScoreSide({ player, frame, me }) {
  const group = frame && frame.groups[player.user_id];
  const live = frame && frame.turn === player.user_id;
  return html`
    <div class=${live ? 'attable' : ''}
         data-testid=${player.user_id === me ? 'side-me' : 'side-them'}>
      <div class="name">${player.name.toUpperCase()}</div>
      <div class="figure">${player.frames}</div>
      ${frame && html`
        <div class=${`group ${group || ''}`} data-testid="live-group">
          ${group ? `ON ${group.toUpperCase()}S` : 'OPEN TABLE'}
        </div>`}
    </div>`;
}

/**
 * Which button is which, for whoever is looking.
 *
 * "me" and "them" while a player is holding their own phone. A tablet plays in
 * nothing, so neither side is "me" and both used to come out as "them" — two
 * elements sharing one name, which reads as an ambiguity to anything selecting
 * by it and made a test reach for .first to dodge the collision.
 */
function wonTestId(who, p1, p2, me) {
  if (who.user_id === me) return 'live-won-me';
  if (p1.user_id === me || p2.user_id === me) return 'live-won-them';
  return who.user_id === p1.user_id ? 'live-won-a' : 'live-won-b';
}

export function Live({ matchId, me, onDone, kiosk = false }) {
  const [live, setLive] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [byShot, setByShot] = useState(false);
  // When the current shot clock started. Not a stored fact — it restarts
  // whenever the match gains a shot or a frame, which is exactly when a
  // player steps up to the table.
  const [shotFrom, setShotFrom] = useState(
    () => new Date().toISOString());
  const [frame, setFrame] = useState(null);
  const [pending, setPending] = useState(null);      // chosen, not yet confirmed
  const [pot, setPot] = useState(emptyPot());
  const [options, setOptions] = useState(false);
  // Declaring a frame won overrules the reducer, so it asks twice. One stray
  // tap must not be able to award a frame nobody played.
  const [sure, setSure] = useState(false);

  useEffect(() => {
    get(`/api/matches/${matchId}`).then((next) => {
      setLive(next);
      // Shots already logged for the frame in progress: replay them, so
      // reopening the app mid-frame puts the table back as it was rather than
      // quietly starting the frame again.
      if (next.shots && next.shots.length) {
        setFrame(rebuild(next));
        setByShot(true);
      }
    }).catch((e) => setError(e.detail || e.message));
  }, [matchId]);

  // Anything recorded resets the shot clock: a frame, or a shot inside one.
  //
  // TODO — the shot clock restarts from when THIS screen noticed the change,
  // not from when the change happened. One scorer never sees the difference.
  // Two do: a tablet on the wall and a player's phone can both score the same
  // match, and each starts its own count when its own fetch came back, so the
  // two clocks drift apart by roughly a round trip and never resync.
  //
  // The fix is to derive it the way the match clock already is — from a
  // timestamp the server sends. That needs the last shot's `ts` in the
  // response, which the log has and _live does not currently return; then this
  // whole local `shotFrom` goes away and both screens count from the same
  // instant. Left until somebody actually scores from two places at once.
  //
  // `frame.history` is in here for a reason found the hard way: confirmShot is
  // fire-and-forget, so a pot or a foul updates the frame ON THIS SCREEN and
  // posts without waiting. `live` does not change until something refetches
  // it, so counting only what the server has told us meant the clock ran on
  // through every shot and reset only when a frame was won — which is the one
  // moment a shot clock does not matter.
  const beats = (live
    ? live.frames.length + Object.values(live.shots_by_frame || {})
        .reduce((n, list) => n + list.length, 0)
    : 0) + (frame && frame.history ? frame.history.length : 0);
  useEffect(() => { setShotFrom(new Date().toISOString()); }, [beats]);

  const act = (fn) => async () => {
    setBusy(true); setError('');
    try { setLive(await fn()); }
    catch (e) { setError(e.detail || e.message); }
    finally { setBusy(false); }
  };

  // Every frame carries its own event_id, so a retry after a dead spot is
  // deduplicated by the log rather than counted twice.
  //
  // The score moves on the tap, not on the reply. This is the most repeated
  // action in the app — somebody stands at a table pressing it after every
  // frame — and it used to sit still for a whole round trip while the server
  // was asked who had just won a frame the person pressing already knew.
  //
  // Honest about being a guess: the server's answer replaces it either way,
  // and a failure puts the old score back rather than leaving a frame on the
  // board that was never recorded. The one thing it does not predict is the
  // END of a match — the reply decides that, so the result screen only ever
  // appears on a frame the log has.
  const wonBy = (userId, clearance = null) => async () => {
    const before = live;
    setBusy(true); setError('');
    setFrame(null); setPending(null); setPot(emptyPot());
    setLive((prev) => (prev ? {
      ...prev,
      players: prev.players.map((p) => (p.user_id === userId
        ? { ...p, frames: p.frames + 1 } : p)),
      // The header counts frames played, so it has to move too or the score
      // advances while "FRAME 3" sits there insisting otherwise.
      frames: [...(prev.frames || []), userId],
    } : prev));
    try {
      setLive(await post(`/api/matches/${matchId}/frame`,
        { winner: userId, clearance, event_id: crypto.randomUUID() }));
    } catch (e) {
      setLive(before);
      setError(e.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Rule 4f: balls potted on a foul are ignored for deciding groups, but they
  // have still left the table — and whether the black is legal later depends
  // on what is actually on it. So a foul carries what went down, if anything.
  const POTTABLE = new Set(['pot', 'foul', 'whitedown']);

  const confirmShot = () => {
    const shot = POTTABLE.has(pending) ? { id: pending, potted: pot }
                                       : { id: pending };
    const next = applyShot(frame, shot);
    setPending(null); setPot(emptyPot());
    setFrame(next);
    // Fire and forget. A shot is history, not a result — if this never lands
    // the frame still counts, so it must not block the tap or put an error on
    // screen over the table.
    post(`/api/matches/${matchId}/shot`,
         { shot, event_id: crypto.randomUUID() }).catch(() => {});
    if (next.over) wonBy(next.winner, clearanceOf(next))();
  };

  // A real undo. The mockup's dropped a line from the log and left the score,
  // the groups and the turn exactly as they were.
  const undoShot = act(async () => {
    const next = await post(`/api/matches/${matchId}/shot/undo`);
    setFrame(rebuild(next));
    setPending(null); setPot(emptyPot());
    return next;
  });

  const undoFrame = act(() => post(`/api/matches/${matchId}/undo`));
  const abandon = act(() => post(`/api/matches/${matchId}/abandon`));

  if (error && !live) {
    return html`
      <p class="error" data-testid="live-error">${error}</p>
      <button class="secondary" onClick=${onDone}>Back</button>`;
  }
  if (!live) return html`<p class="sub">Loading…</p>`;

  const [p1, p2] = live.players;
  const target = live.race_to;
  const format = (live.game_format || '').toUpperCase();

  if (live.status !== 'in_progress') {
    const winner = p1.frames > p2.frames ? p1 : p2;
    const drawn = p1.frames === p2.frames;
    return html`
      ${/* On the wall this screen has nobody to dismiss it: the players have
            walked away and the next pair are waiting. It takes itself off. */''}
      ${kiosk && html`<${GoBack} after=${15} onDone=${onDone} />`}
      <div class="screen" style="align-items:center;text-align:center;gap:1rem">
        <div style="color:var(--brass)"><${Icon} name="trophy" size=${40} /></div>
        <h1 class="gold">${drawn ? 'ALL SQUARE' : `${winner.name.toUpperCase()} WINS`}</h1>
        <div class="figure" data-testid="live-final">
          ${`${p1.frames} — ${p2.frames}`}
        </div>
        <p class="sub mono">${`${format} · RACE TO ${target}`}</p>
        <p class="sub">
          ${live.status === 'provisional'
            ? 'Waiting for them to confirm it before it counts.'
            : 'Counted.'}
        </p>
        <button data-testid="live-done" onClick=${onDone}>
          ${kiosk ? 'Done' : 'Back to the app'}
        </button>

        ${/* What actually happened, for a match that recorded it. This screen
              used to end at the score, so a frame scored shot by shot was
              written down and then never shown to anybody. */''}
        <div style="text-align:left;width:100%">
          <${Frames} live=${live} />
        </div>
      </div>`;
  }

  const nameOf = (id) => (live.players.find((p) => p.user_id === id) || {}).name || '';
  const shots = shotsFor(frame);
  const log = matchLog(live, frame);
  const canConfirm = pending && !(pending === 'pot' && potIsEmpty(pot));

  const bump = (key) => () => setPot((p) => (
    key === 'black'
      ? { ...p, black: !p.black }
      // Capped at what is left, so a count can never describe a table that
      // cannot exist. Past the cap it wraps to zero rather than sticking.
      : { ...p, [key]: p[key] + 1 > frame.remaining[key] ? 0 : p[key] + 1 }));

  const Chip = ({ id, label, cls }) => html`
    <button class=${`${cls} ${(id === 'black' ? pot.black : pot[id]) ? 'on' : ''}`}
            data-testid=${`pot-${id}`} onClick=${bump(id)}>
      <span class="dot" style=${`background:var(--${cls === 'black' ? 'ivory' : cls})`}></span>
      ${label}${id !== 'black' && pot[id] ? ` ×${pot[id]}` : ''}
    </button>`;

  // ---- the tablet on the wall -----------------------------------------
  //
  // Landscape, and read from across a room rather than held. The phone screen
  // stacks a small centred scoreline over a column of controls, which is right
  // in the hand and wrong on a wall: the number somebody is checking from the
  // baulk end ends up the smallest thing on it.
  //
  // Scores at the edges where they cannot be reached over, the only two
  // buttons that matter square in the middle where they can.
  //
  // Shot by shot is NOT here at all. It is a minute of careful tapping per
  // frame, which is a thing to do sitting down with your own phone, not
  // standing at a wall in front of whoever is waiting to break. The tablet
  // opens the match — a phone may not, at a venue — and from that moment
  // either can score it: the tablet in one tap, or a player from the match
  // waiting on their home screen. The permission for those two is already the
  // same, and this is the shape of screen each of them deserves.
  // The match on a wall is the same match, laid out for a room instead of a
  // hand: the scores move to the edges and everything else keeps its place in
  // the middle. One definition of the controls, because two versions of a
  // shot pad with fouls, free shots and ball counts in it would be two things
  // to keep in step and one of them would drift.
  const sideOf = (p) => html`
    <div class=${`wall ${frame && frame.turn === p.user_id ? 'attable' : ''}`}
         data-testid=${p.user_id === me ? 'side-me' : 'side-them'}>
      <div class="name">${p.name.toUpperCase()}</div>
      <div class="figure">${p.frames}</div>
      ${frame && html`
        <div class=${`group ${frame.groups[p.user_id] || ''}`}
             data-testid="live-group">
          ${frame.groups[p.user_id]
            ? `ON ${frame.groups[p.user_id].toUpperCase()}S` : 'OPEN TABLE'}
        </div>`}
      ${frame && html`
        <div class="left" data-testid=${`wall-left-${p.user_id}`}>
          ${`${frame.remaining[frame.groups[p.user_id] || RED]} LEFT`}
        </div>`}
    </div>`;
  return html`
    <div class="matchbar">
      <button data-testid="live-exit" onClick=${onDone} aria-label="Leave the match">
        <${Icon} name="x" size=${18} />
      </button>
      <span data-testid="live-frame">
        ${`FRAME ${live.frames.length + 1} · RACE TO ${target} · ${format}`}
      </span>
      <button data-testid="live-rules" aria-label="Rules"
              onClick=${() => { location.hash = '#/rules'; }}>
        <${Icon} name="help-circle" size=${18} />
      </button>
    </div>

    ${/* On a phone the scoreline sits above the controls; on a wall the two
          scores go to the edges and the controls between them. Same controls
          either way — `plain` is display:contents, so on a phone these two
          wrappers are not there at all. */''}
    ${(live.shot_seconds > 0 || live.match_seconds > 0) && html`
      <div class="clocks" data-testid="clocks">
        <${Clock} label="Shot" seconds=${live.shot_seconds}
                  from=${shotFrom} testid="shot-clock" />
        <${Clock} label="Match" seconds=${live.match_seconds}
                  from=${live.started_at} testid="match-clock" />
      </div>`}

    ${!kiosk && html`<${Score} live=${live} frame=${frame} me=${me} />`}

    <div class=${kiosk ? 'wallscreen' : 'plain'}
         data-testid=${kiosk ? 'wall-screen' : 'phone-screen'}>
    ${kiosk && sideOf(live.players[0])}
    <div class=${kiosk ? 'wallacts' : 'plain'}>

    ${frame && html`
      <div class="balls" data-testid="shot-balls">
        <span data-testid="balls-red">
          <span class="dot" style="background:var(--red)"></span>
          ${`${frame.remaining[RED]} LEFT`}</span>
        <span data-testid="balls-yellow">
          <span class="dot" style="background:var(--yellow)"></span>
          ${`${frame.remaining[YELLOW]} LEFT`}</span>
      </div>`}

    <div class="turn">
      ${frame
        ? html`<span class=${`tag ${frame.visitsLeft > 1 ? 'owed' : ''}`}
                     data-testid="shot-turn">
                 ${nameOf(frame.turn).toUpperCase()}
                 ${frame.stage === 'break' ? "'S BREAK" : "'S TURN"}
                 ${/* Rule 6b changes what is legal for exactly one shot — any
                       ball may be hit and potted — so the badge has to say
                       which shot that is, not just that a penalty is owed. */''}
                 ${frame.freeShot ? ' · FREE SHOT'
                   : frame.visitsLeft > 1 ? ' · ONE VISIT OWED' : ''}
               </span>`
        : html`<span class="tag" data-testid="live-breaks">
                 ${`${nameOf(live.breaks_next).toUpperCase()}'S BREAK`}
               </span>`}

      ${frame && html`
        <button class="secondary dots" data-testid="shot-options"
                aria-expanded=${options} aria-label="More"
                onClick=${() => { setOptions(!options); setSure(false); }}>
          <${Icon} name="more-vertical" size=${14} />
        </button>`}

      ${frame && options && html`
        <div class="menu" data-testid="shot-menu">
          ${frame.stage === 'break'
            ? html`<p class="sub" style="margin:0;text-align:center"
                      data-testid="shot-menu-empty">
                     Nothing to declare during the break
                   </p>`
            : html`
              ${/* It overrules the reducer, so it asks twice: one stray tap
                    must not be able to award a frame nobody played. */''}
              <button class="secondary" data-testid="shot-declare"
                      style=${`margin:0;color:var(--brass);
                               ${sure ? 'background:#241A08' : ''}`}
                      onClick=${() => (sure
                        ? (() => { setOptions(false); setSure(false);
                                   wonBy(frame.turn)(); })()
                        : setSure(true))}>
                <${Icon} name="trophy" size=${14} />
                <span>
                  ${sure ? `Tap again — ${nameOf(frame.turn)}`
                         : `Declare frame won — ${nameOf(frame.turn)}`}
                </span>
              </button>`}
        </div>`}
    </div>

    <div class="screen" style="gap:.75rem;padding-top:0">
      ${frame ? html`
        <div class="events">
          ${shots.map((s) => html`
            <button key=${s.id} class=${s.tone || ''}
                    aria-pressed=${pending === s.id}
                    data-testid=${`shot-${s.id}`}
                    onClick=${() => { setPending(pending === s.id ? null : s.id);
                                      setPot(emptyPot()); }}>
              <${Icon} name=${s.icon} />
              <span>${s.label.toUpperCase()}</span>
            </button>`)}
        </div>

        ${POTTABLE.has(pending) && html`
          ${pending !== 'pot' && html`
            <p class="sub" data-testid="foul-pots-hint" style="text-align:center">
              Anything that went down on the foul — leave it blank if nothing did.
            </p>`}
          <div class="chips">
            <${Chip} id=${RED} label="RED" cls="red" />
            <${Chip} id=${YELLOW} label="YELLOW" cls="yellow" />
            <${Chip} id="black" label="BLACK" cls="black" />
          </div>`}

        ${/* The label names what is about to be recorded, but only once there
              is something: describe({}) is "Nothing down", which is the name
              of a DIFFERENT event, and reading it on a disabled button while
              "Balls down" is lit says the opposite of what is true. */''}
        <button disabled=${!canConfirm} data-testid="shot-confirm"
                onClick=${confirmShot}>
          ${POTTABLE.has(pending) && !potIsEmpty(pot) ? describe(pot) : 'Confirm'}
        </button>

        <button class="secondary" data-testid="shot-off"
                onClick=${() => { setByShot(false); setFrame(null); setPending(null); }}>
          Just tell me who won
        </button>
      ` : html`
        <label>Who won that frame</label>
        ${/* Wrapped so the wall can put the two names side by side. `whowon`
              is display:contents on a phone, where stacked is right. */''}
        <div class="whowon">
          <button data-testid=${wonTestId(p1, p1, p2, me)}
                  disabled=${busy} onClick=${wonBy(p1.user_id)}>
            ${`${p1.name} won it`}
          </button>
          <button class="secondary"
                  data-testid=${wonTestId(p2, p1, p2, me)}
                  disabled=${busy} onClick=${wonBy(p2.user_id)}>
            ${`${p2.name} won it`}
          </button>
        </div>
        <button class="secondary" data-testid="shot-on"
                onClick=${() => { setFrame(rebuild(live)); setByShot(true); }}>
          Score it shot by shot
        </button>`}

      <div>
        <div class="rowbetween" style="margin-bottom:.5rem">
          <label style="margin:0">Event log</label>
          ${frame && frame.history.length > 0 && html`
            <button class="secondary" data-testid="shot-undo"
                    style="width:auto;padding:.3rem .6rem;margin:0"
                    disabled=${busy} onClick=${undoShot}>
              <${Icon} name="undo-2" size=${12} /> Undo
            </button>`}
        </div>
        <div class="log">
          ${log.length === 0
            ? html`<p class="empty">No shots yet</p>`
            : log.slice(0, 30).map((h, i) => html`
                <div class="card" key=${i} data-testid="shot-log">
                  <span class="who">
                    ${`F${h.frame}`}
                    ${!h.start && ` · ${nameOf(h.player).toUpperCase()}`}
                    ${/* The colour a player is on, from the shot that decided
                          it onwards, so the log reads as the frame did. */''}
                    ${h.group && html`
                      <i class=${`grp ${h.group}`} data-testid="log-group"
                         title=${`on ${h.group}s`}>
                        ${h.group === RED ? 'R' : 'Y'}
                      </i>`}
                  </span>
                  <span>${h.text}</span>
                </div>`)}
        </div>
      </div>

      ${/* Here, not on the result screen where it used to be. The token is
            honoured only while a match is in progress, so a QR shown after the
            final frame is one that answers 404 to everybody who scans it. */''}
      <${WatchQr} matchId=${matchId} />

      <div class="row">
        <button class="secondary" data-testid="live-undo"
                disabled=${busy || live.frames.length === 0} onClick=${undoFrame}>
          Undo frame
        </button>
        <button class="secondary" data-testid="live-abandon"
                disabled=${busy} onClick=${abandon}>Stop here</button>
      </div>

      ${error && html`<p class="error" data-testid="error">${error}</p>`}
    </div>
    </div>
    ${kiosk && sideOf(live.players[1])}
    </div>`;
}


/**
 * Counts down and then leaves.
 *
 * The end screen on a wall is read by people who did not tap anything to get
 * there and will not tap anything to leave — the winner has gone to the bar.
 * Fifteen seconds is long enough to read the score and short enough that the
 * next pair are not standing in front of somebody else's result.
 *
 * It says how long it has, because a screen that changes on its own without
 * warning reads as a fault.
 */
function GoBack({ after, onDone }) {
  const [left, setLeft] = useState(after);
  useEffect(() => {
    const tick = setInterval(() => setLeft((n) => n - 1), 1000);
    const done = setTimeout(onDone, after * 1000);
    return () => { clearInterval(tick); clearTimeout(done); };
  }, []);
  return html`
    <p class="sub mono" data-testid="live-autoback">
      ${`Back to the venue in ${Math.max(0, left)}s`}
    </p>`;
}
